import { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { PageHeader } from '../layout/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { useWeekStats, sumWeekTotals } from '../../lib/weekStats';
import { dateKey } from '../../lib/setProgress';
import { WeekHeatmap } from './WeekHeatmap';
import { DayProgressRow } from './DayProgressRow';

export function ProgressView(): JSX.Element {
  const today = useMemo(() => new Date(), []);
  const todayKey = useMemo(() => dateKey(today), [today]);
  const stats = useWeekStats(today);
  const totals = useMemo(() => sumWeekTotals(stats), [stats]);
  const isWeekEmpty = totals.totalSetsDone === 0;
  const weekRange = formatWeekRange(stats);

  return (
    <main className="min-h-dvh bg-zinc-50 text-zinc-900 font-sans">
      <PageHeader title="Fortschritt" eyebrow={weekRange} />

      <div className="mx-auto max-w-3xl px-4 pt-4 pb-24 md:px-6 md:pb-8">
        <section className="rounded-xl border border-zinc-200 bg-white p-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-zinc-600">Diese Woche</h2>
            <p className="text-sm font-semibold tabular-nums text-zinc-900">
              {totals.completedExercises}/{totals.totalExercises} Uebungen ·{' '}
              {totals.totalSetsDone}/{totals.totalSets} Sets
            </p>
          </div>
          <div className="mt-3">
            <WeekHeatmap stats={stats} todayKey={todayKey} />
          </div>
        </section>

        {isWeekEmpty ? (
          <EmptyState
            Icon={TrendingUp}
            heading="Noch keine Trainings absolviert"
            body="Starte heute dein erstes Training, um Fortschritt zu sehen."
          />
        ) : null}

        <h2 className="mt-6 mb-3 text-xl font-semibold leading-tight text-zinc-900">
          Tagesuebersicht
        </h2>
        <ol className="flex flex-col gap-2">
          {stats.map((s) => (
            <DayProgressRow
              key={s.dayKey}
              stats={s}
              isToday={s.dayKey === todayKey}
            />
          ))}
        </ol>
      </div>
    </main>
  );
}

function formatWeekRange(stats: readonly { date: Date }[]): string {
  if (stats.length === 0) return '';
  const start = stats[0]?.date;
  const end = stats[stats.length - 1]?.date;
  if (!start || !end) return '';
  const fmt = (d: Date): string =>
    `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
  return `Woche ${fmt(start)}–${fmt(end)}`;
}
